import { useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ECUADOR_BANKS, BANK_OTHER } from "@/lib/banksEcuador";

/**
 * Selector de banco/COAC ecuatoriano.
 *
 * - Recibe `value` (string) y `onChange(newValue)`.
 * - Si `value` no está en la lista, arranca en modo "Otro" con el input
 *   visible y pre-cargado — esto preserva compatibilidad con registros
 *   viejos guardados como texto libre (Banco Pichincha con acentos raros,
 *   variantes escritas por usuarios, etc.).
 * - El option "Otro (escribir manualmente)" siempre está al final para
 *   cubrir instituciones que no estén en la lista aún.
 *
 * data-testid: se propaga al trigger para que el testing agent pueda
 * driver el flujo por selectores estables.
 */
export function BankSelect({ value, onChange, testId = "bank-select", required = false, className = "" }) {
  const isKnown = useMemo(() => ECUADOR_BANKS.includes(value), [value]);
  const showOther = value !== "" && !isKnown;
  const selectValue = showOther ? BANK_OTHER : value;

  const handleSelect = (v) => {
    if (v === BANK_OTHER) {
      // Deja `value` para que el input aparezca vacío y el usuario escriba.
      onChange("");
      // Truco para que el input reciba foco después del render.
      requestAnimationFrame(() => {
        document.querySelector(`[data-testid="${testId}-other-input"]`)?.focus();
      });
    } else {
      onChange(v);
    }
  };

  return (
    <div className={className}>
      <Select value={selectValue} onValueChange={handleSelect}>
        <SelectTrigger
          data-testid={testId}
          className="w-full bg-[#171717] border-[#262626] h-10 text-sm"
          aria-required={required}
        >
          <SelectValue placeholder="Selecciona tu banco o cooperativa" />
        </SelectTrigger>
        <SelectContent className="bg-[#0F0F0F] border-[#262626] max-h-72">
          {ECUADOR_BANKS.map((b) => (
            <SelectItem key={b} value={b} data-testid={`${testId}-option-${b}`}>
              {b}
            </SelectItem>
          ))}
          <SelectItem value={BANK_OTHER} data-testid={`${testId}-option-other`}>
            Otro (escribir manualmente)
          </SelectItem>
        </SelectContent>
      </Select>

      {showOther && (
        <Input
          data-testid={`${testId}-other-input`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Escribe el nombre de tu banco o cooperativa"
          required={required}
          className="bg-[#171717] border-[#262626] mt-2"
        />
      )}
    </div>
  );
}

export default BankSelect;
